import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStore, useDeliveryFacts } from "./StoreContext";
import { storeApi } from "./storeApi";
import { money } from "./format";
import ProductCard from "./ProductCard";
import Icon from "./Icons";
import useSeo from "./useSeo";

// Server-priced view of the sellable lines (sales, the discount code).
export function useQuote({ items, code, extra = {}, enabled = true }) {
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const key = JSON.stringify([items, code, extra]);
  useEffect(() => {
    if (!enabled || !items.length) {
      setQuote(null);
      return undefined;
    }
    let live = true;
    setBusy(true);
    storeApi
      .quote({ items, discountCode: code || undefined, ...extra })
      .then((q) => live && (setQuote(q), setError(null)))
      .catch((e) => live && setError(e))
      .finally(() => live && setBusy(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);
  return { quote, error, busy };
}

export default function CartPage() {
  const { lines, sellable, blocked, setQty, remove, removeBlocked, code, setCode, products, catalog } = useStore();
  const navigate = useNavigate();
  const delivery = useDeliveryFacts();
  const [codeInput, setCodeInput] = useState(code);
  const [codeOpen, setCodeOpen] = useState(!!code);
  const items = sellable.map((l) => ({ productId: l.id, qty: l.qty }));
  const { quote, error } = useQuote({ items, code, enabled: !!catalog });
  useSeo({ title: "Cart", noindex: true, path: "/merch/cart" });

  // A bad code shouldn't block the cart: drop it and say why.
  const [codeError, setCodeError] = useState(null);
  useEffect(() => {
    if (error && code) {
      setCodeError(error.message);
      setCode("");
    }
  }, [error, code, setCode]);

  const issues = quote?.stockIssues || [];
  const quoteProblem = error && !code ? error.message : null;
  const subtotal = quote ? quote.subtotalMinor : sellable.reduce((s, l) => s + (l.priceMinor || 0) * l.qty, 0);
  const afterDiscount = subtotal - (quote?.discountMinor || 0);
  const free = delivery.freeOverMinor;
  const toFree = free != null ? Math.max(0, free - afterDiscount) : null;
  const canCheckout = sellable.length > 0 && !blocked.length && !issues.length && !quoteProblem;

  if (!lines.length) {
    const picks = products.filter((p) => p.inStock).slice(0, 4);
    return (
      <div className="sh-wrap sh-cart-empty">
        <h1 className="sh-h1">Your cart is empty</h1>
        <Link className="sh-btn" to="/merch">
          Continue shopping
        </Link>
        {picks.length > 0 && (
          <section className="sh-related">
            <h2 className="sh-h2">Worth a look</h2>
            <div className="sh-grid sh-grid-4">
              {picks.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className="sh-wrap sh-cart">
      <header className="sh-pagehead">
        <h1 className="sh-h1">
          Cart <span className="sh-muted">({lines.reduce((s, l) => s + l.qty, 0)})</span>
        </h1>
        <Link className="sh-textlink" to="/merch">
          Continue shopping
        </Link>
      </header>

      <div className="sh-cart-grid">
        <div>
          {quoteProblem && (
            <div className="sh-alert" role="alert">
              <p>{quoteProblem}</p>
            </div>
          )}
          {blocked.length > 0 && (
            <div className="sh-alert" role="alert">
              <p>
                {blocked.length === 1 ? "One item in your cart can't be bought right now." : `${blocked.length} items in your cart can't be bought right now.`}
              </p>
              <button className="sh-textlink" onClick={removeBlocked}>
                Remove {blocked.length === 1 ? "it" : "them"}
              </button>
            </div>
          )}
          <ul className="sh-lines">
            {lines.map((l) => {
              const q = quote?.lines.find((x) => x.productId === l.id);
              const issue = issues.find((i) => i.productId === l.id);
              const unit = q ? q.unitPriceMinor : l.priceMinor;
              const opts = Object.values(l.options || {}).filter(Boolean).join(" / ");
              const dead = l.state !== "ok" && l.state !== "loading";
              return (
                <li key={l.id} className={`sh-line${dead ? " is-dead" : ""}`}>
                  <Link to={l.slug ? `/merch/${l.slug}` : "/merch"} className="sh-line-img">
                    {l.image ? <img src={l.image} alt="" loading="lazy" /> : <span />}
                  </Link>
                  <div className="sh-line-main">
                    <div className="sh-line-top">
                      <div>
                        <Link to={l.slug ? `/merch/${l.slug}` : "/merch"} className="sh-line-title">
                          {l.title}
                        </Link>
                        {opts && <p className="sh-line-opts">{opts}</p>}
                      </div>
                      <p className="sh-line-total">{dead ? "" : money((unit || 0) * l.qty)}</p>
                    </div>
                    {!dead && (
                      <p className="sh-line-unit">
                        {money(unit)}
                        {q && q.basePriceMinor > q.unitPriceMinor && <s>{money(q.basePriceMinor)}</s>}
                      </p>
                    )}
                    <LineNote line={l} issue={issue} />
                    <div className="sh-line-actions">
                      {!dead && (
                        <div className="sh-qty" role="group" aria-label={`Quantity of ${l.title}`}>
                          <button onClick={() => setQty(l.id, l.qty - 1)} aria-label="One less">
                            <Icon name="minus" size={14} />
                          </button>
                          <span aria-live="polite">{l.qty}</span>
                          <button onClick={() => setQty(l.id, l.qty + 1)} disabled={l.qty >= l.max} aria-label="One more">
                            <Icon name="plus" size={14} />
                          </button>
                        </div>
                      )}
                      <button className="sh-textlink sh-remove" onClick={() => remove(l.id)}>
                        Remove
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <aside className="sh-summary">
          <h2 className="sh-h3">Summary</h2>
          {toFree != null && sellable.length > 0 && (
            <div className="sh-freebar">
              <p>{toFree > 0 ? <>Add {money(toFree)} more for free delivery</> : <><Icon name="check" size={14} /> Free delivery in Czechia</>}</p>
              <span>
                <i style={{ width: `${Math.min(100, (afterDiscount / free) * 100)}%` }} />
              </span>
            </div>
          )}
          <dl className="sh-totals">
            <div>
              <dt>Subtotal</dt>
              <dd>{money(subtotal)}</dd>
            </div>
            {quote?.discountMinor > 0 && (
              <div className="sh-total-discount">
                <dt>{quote.discount?.code || quote.discount?.name || "Discount"}</dt>
                <dd>−{money(quote.discountMinor)}</dd>
              </div>
            )}
            <div>
              <dt>Delivery</dt>
              <dd className="sh-muted">{toFree === 0 ? "Free" : delivery.fromMinor != null ? `from ${money(delivery.fromMinor)}` : "At checkout"}</dd>
            </div>
            <div className="sh-total">
              <dt>Total</dt>
              <dd>{money(afterDiscount)}</dd>
            </div>
          </dl>

          {codeOpen ? (
            <form
              className="sh-code"
              onSubmit={(e) => {
                e.preventDefault();
                setCodeError(null);
                setCode(codeInput.trim());
              }}
            >
              <label className="sh-sr" htmlFor="sh-code">
                Discount code
              </label>
              <input id="sh-code" value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())} placeholder="Discount code" autoComplete="off" />
              <button className="sh-btn sh-btn-ghost" disabled={!codeInput.trim() || codeInput.trim() === code}>
                Apply
              </button>
              {code && quote?.discount && (
                <p className="sh-ok">
                  <Icon name="check" size={14} /> {quote.discount.name || code} applied ·{" "}
                  <button type="button" className="sh-textlink" onClick={() => {
                      setCode("");
                      setCodeInput("");
                    }}>
                    Remove
                  </button>
                </p>
              )}
              {codeError && <p className="sh-error">{codeError}</p>}
            </form>
          ) : (
            <button className="sh-textlink sh-code-toggle" onClick={() => setCodeOpen(true)}>
              Have a discount code?
            </button>
          )}

          <button className="sh-btn sh-btn-xl" disabled={!canCheckout} onClick={() => navigate("/merch/checkout")}>
            <Icon name="lock" size={16} /> Checkout
          </button>
          {!canCheckout && (blocked.length > 0 || issues.length > 0) && <p className="sh-muted sh-small">Sort out the items marked above to continue.</p>}
          <p className="sh-summary-note">
            {delivery.card ? "Card, Apple Pay, Google Pay or bank transfer." : "Bank transfer with a QR code."} {delivery.returnDays}-day returns.
          </p>
        </aside>
      </div>
    </div>
  );
}

function LineNote({ line: l, issue }) {
  if (l.state === "unavailable") return <p className="sh-line-note bad">No longer available</p>;
  if (l.state === "soldout") return <p className="sh-line-note bad">Sold out since you added it</p>;
  if (l.state === "reserved") return <p className="sh-line-note bad">Held in someone else's checkout right now</p>;
  if (issue) return <p className="sh-line-note bad">{issue.available ? `Only ${issue.available} left. Lower the quantity to continue.` : "Just sold out"}</p>;
  if (l.adjusted) return <p className="sh-line-note warn">Only {l.adjusted.to} left, so we lowered it from {l.adjusted.from}</p>;
  if (l.priceWas != null && l.priceWas > l.priceMinor) return <p className="sh-line-note good">Cheaper than when you added it</p>;
  if (l.priceWas != null) return <p className="sh-line-note warn">Price went up from {money(l.priceWas)} since you added it</p>;
  if (l.state === "ok" && l.max && l.qty >= l.max && l.max <= 3) return <p className="sh-line-note warn">That's the last {l.max === 1 ? "one" : l.max}</p>;
  return null;
}
